import { AuthContext } from '@repo-level/core-front';
import type { IMissionCard } from 'api/cms/missions';
import type { TMissionsProgressResponse } from 'api/missions';
import { useUserCanIssueBonuses } from 'hooks/bonuses';
import { useContext, useMemo } from 'react';
import {
  createResult,
  DEFAULT_RESULT,
  MAX_PROGRESS,
  OK_BUTTON,
} from '../constants';
import {
  getCashBonusResult,
  getCpNotificationResult,
  getFreeSpinsResult,
} from '../helpers/notification';
import { MissionRewardType, NotificationState } from '../types';
import { useMissionInfo } from './useMissionInfo';

export interface INotificationButton {
  link?: string;
  action?: 'exchangeCp' | 'close';
}

export interface INotificationStateResultProps {
  notificationState: NotificationState;
  messageKey: string | null;
  button?: INotificationButton;
  rewardType: MissionRewardType;
  canExchangeCp: boolean;
}

export const useNotificationState = (
  mission: IMissionCard,
  progress?: TMissionsProgressResponse
): INotificationStateResultProps => {
  const { compPoints } = useContext(AuthContext);
  const isUserCanIssueBonuses = useUserCanIssueBonuses();

  const { isMissionDone, rewardType, bonusStage } = useMissionInfo(
    mission,
    progress
  );

  const canExchangeCp = useMemo(() => {
    const userCp = compPoints?.chargeable?.points ?? 0;
    return userCp >= MAX_PROGRESS;
  }, [compPoints]);

  const notificationResult = useMemo(() => {
    if (!isMissionDone) return DEFAULT_RESULT;

    if (!isUserCanIssueBonuses) {
      return createResult(
        NotificationState.BONUSES_DISABLED,
        'notification.bonusesDisabled',
        OK_BUTTON
      );
    }

    if (rewardType === MissionRewardType.CP) {
      return getCpNotificationResult(canExchangeCp);
    }

    if (rewardType === MissionRewardType.CASH_BONUS) {
      return getCashBonusResult(bonusStage ?? null);
    }

    if (rewardType === MissionRewardType.FREE_SPINS) {
      return getFreeSpinsResult(bonusStage ?? null);
    }

    return DEFAULT_RESULT;
  }, [
    isMissionDone,
    isUserCanIssueBonuses,
    rewardType,
    bonusStage,
    canExchangeCp,
  ]);

  return {
    ...notificationResult,
    rewardType,
    canExchangeCp,
  };
};
